import { DashboardNavbar } from '@app/dashboard/dashboard-navbar';
import { AdHost } from '@common/admin/ads/ad-host';
import { useAuth } from '@common/auth/use-auth';
import {Trans} from '@common/i18n/trans';
import { useTrans } from '@common/i18n/use-trans';
import {StaticPageTitle} from '@common/seo/static-page-title';
import { Accordion, AccordionItem } from '@common/ui/accordion/accordion';
import { Button } from '@common/ui/buttons/button';
import { Footer } from '@common/ui/footer/footer';
import { List, ListItem } from '@common/ui/list/list';
import { DialogTrigger } from '@common/ui/overlays/dialog/dialog-trigger';
import { AnimatePresence, m} from 'framer-motion';
import React, {Fragment, ReactNode, useEffect, useState} from 'react';
import { useMyTeam } from '@app_modules/requests/use-my-team';
import { PersonalTeam } from '@app_modules/teams/user-teams';
import { ConfirmationDialog } from '@common/ui/overlays/dialog/confirmation-dialog';
import { useDeleteTeam } from '@app_modules/requests/delete-team';
import { useDialogContext } from '@common/ui/overlays/dialog/dialog-context';

import { Team } from '@app_modules/teams/team';
import { LeaveTeamConfirmation } from '@app_modules/teams/leave-team-confirmation';
import { useRemoveMember } from '@app_modules/requests/remove-member';
import { Menu, MenuItem, MenuTrigger } from '@common/ui/navigation/menu/menu-trigger';
import { openDialog } from '@common/ui/overlays/store/dialog-store';
import { DeleteIcon } from '@common/icons/material/Delete';
import { UpdateTeamDialog } from '@app_modules/teams/update-team-dialog';
import { ExitToAppIcon } from '@common/icons/material/ExitToApp';
import { EditIcon } from '@common/icons/material/Edit';
import { RenameTeamDialog } from '@app_modules/teams/rename-team-dialog';
import { PersonAddIcon } from '@common/icons/material/PersonAdd';
import { TeamMembersDialog } from '@app_modules/requests/team-members-dialog';
import { KeyboardArrowDownIcon } from '@common/icons/material/KeyboardArrowDown';
import { ButtonSize } from '@common/ui/buttons/button-size';
import { useValueLists } from '@common/http/value-lists';
import { ArrowDropDownIcon } from '@common/icons/material/ArrowDropDown';
import { TeamMember } from '@app_modules/teams/team-member';
import { TeamInvite } from '@app_modules/teams/team-invite';
import { useResendInvite } from '@app_modules/requests/resend-invite';
import { CloseIcon } from '@common/icons/material/Close';
import { IconButton } from '@common/ui/buttons/icon-button';
import { toast } from '@common/ui/toast/toast';
import { message } from '@common/i18n/message';
import { ChipField, ChipValue } from '@common/ui/forms/input-field/chip-field/chip-field';
import { useInviteMembers } from '@app_modules/requests/invite-members';
import { GroupIcon } from '@common/icons/material/Group';
import { Tooltip } from '@common/ui/tooltip/tooltip';
import { CreateTeamDialog } from '@app_modules/teams/create-team-dialog';
import { DataTableAddItemButton } from '@common/datatable/data-table-add-item-button';
import { useChangeRole } from '@app_modules/requests/change-role';
import { isEmail } from '@common/utils/string/is-email';
import { MyTeamView } from './my-team-view';
import { TeamsIndexPage } from '@app_modules/teams/teams-index-page';


export function MyTeamPage() {
  const {data: team} = useMyTeam();
  const myTeam = team?.team;

  const {trans} = useTrans();
  const {user} = useAuth();
  // const query = useProjects({userId: user!.id, paginate: 'lengthAware'});
  // const {sortDescriptor, setSortDescriptor, searchQuery, setSearchQuery} =
  //   query;
  // const sortValue = `${sortDescriptor.orderBy}|${sortDescriptor.orderDir}`;

  const [selectorIsOpen, setSelectorIsOpen] = useState(false);


  return (
    <Fragment>
      <StaticPageTitle>
        <Trans message="Team" />
      </StaticPageTitle>
      <div className="flex h-screen flex-col overflow-y-scroll">
        <DashboardNavbar />
        <div className="relative">
          <div className="mx-auto flex-auto px-12">
            <AdHost slot="dashboard_top" className="mb-36" />
            <AnimatePresence initial={false} mode="wait">
              {/*  */}
              {user?.can_create_team ? (<>
                  <TeamsIndexPage/>
                </>) : (<>
                  <div className="min-h-full gap-12 overflow-x-hidden p-12 md:gap-24 md:p-24">
                    <div className="mb-24 items-center justify-between gap-24 md:flex">
                      <StaticPageTitle><Trans message="Team" /></StaticPageTitle>
                      <h1 className="mb-24 text-3xl font-light md:mb-0"><Trans message="Team" /></h1>
                    </div>
                    <MyTeamView/>
                  </div>
                </>)}
            </AnimatePresence>
            <AdHost slot="dashboard_bottom" className="mt-36" />
          </div>
        </div>
        <Footer className="mt-40 px-40" />
      </div>
    </Fragment>
  );
}


interface ManageButtonProps {
  setSelectorIsOpen: (value: boolean) => void;
  team: Team;
  onChange?: (id: number) => void;
}
function ManageButton({
  setSelectorIsOpen,
  team,
  onChange,
}: ManageButtonProps) {
  const {user} = useAuth();

  return (
    <MenuTrigger onItemSelected={() => setSelectorIsOpen(false)}>
      <Button
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        color="primary"
        size="xs"
        variant="outline"
        endIcon={<KeyboardArrowDownIcon />}
      >
        <Trans message="Manage" />
      </Button>
      <Menu>
        <MenuItem
          onClick={e => {
            e.stopPropagation();
            openDialog(TeamMembersDialog, {team});
          }}
          value="teamMembers"
          startIcon={<PersonAddIcon />}
        >
          <Trans message="Members" />
        </MenuItem>
        {team.owner_id === user?.id && (
          <MenuItem
            onClick={e => {
              e.stopPropagation();
              openDialog(RenameTeamDialog, {team});
            }}
            value="updateTeam"
            startIcon={<EditIcon />}
          >
            <Trans message="Rename" />
          </MenuItem>
        )}
        {team.owner_id !== user?.id && (
          <MenuItem
            onClick={e => {
              e.stopPropagation();
              <UpdateTeamDialog team={team} />
              // openDialog(UpdateTeamDialog, {team, onChange});
            }}
            value="leaveTeam"
            startIcon={<ExitToAppIcon />}
          >
            <Trans message="Leave" />
          </MenuItem>
        )}
        {team.owner_id === user?.id && (
          <MenuItem
            onClick={e => {
              e.stopPropagation();
              openDialog(DeleteTeamConfirmation, {team, onChange});
            }}
            value="deleteTeam"
            startIcon={<DeleteIcon />}
          >
            <Trans message="Delete" />
          </MenuItem>
        )}
        
      </Menu>
    </MenuTrigger>
  );
}

interface LeaveTeamDialogProps {
  team: Team;
  onChange?: (id: number) => void;
}
function LeaveTeamDialog({
  team,
  onChange,
}: LeaveTeamDialogProps) {
  const removeMember = useRemoveMember();
  const {user} = useAuth();
  const {close} = useDialogContext();
  return (
    <LeaveTeamConfirmation
      isLoading={removeMember.isPending}
      onConfirm={() => {
        removeMember.mutate(
          {
            teamId: team.id,
            memberId: user!.id,
            memberType: 'member',
          },
          {
            onSuccess: () => {
              close();
              onChange?.(PersonalTeam.id);
            },
          },
        );
      }}
    />
  );
}


interface DeleteTeamConfirmationProps {
  team: Team;
  onChange?: (id: number) => void;
}
function DeleteTeamConfirmation({
  team,
  onChange,
}: DeleteTeamConfirmationProps) {
  const deleteTeam = useDeleteTeam();
  const {close} = useDialogContext();
  return (
    <ConfirmationDialog
      isDanger
      title={<Trans message="Delete team" />}
      body={
        <Trans
          message="Are you sure you want to delete “:name“?"
          values={{name: team.name}}
        />
      }
      confirm={<Trans message="Delete" />}
      isLoading={deleteTeam.isPending}
      onConfirm={() => {
        deleteTeam.mutate(
          {id: team.id},
          {
            onSuccess: () => {
              close();
              onChange?.(PersonalTeam.id);
            },
          },
        );
      }}
    />
  );
}

interface TeamManagerProps {
  team: Team;
}
function TeamManager({team}: {team: Team}) {
  const {user} = useAuth();
  const can = usePermissions(team);


  
  const members: (TeamMember | TeamInvite)[] = [
    ...(team.members || []),
    ...(team.invites || []),
  ];
  const shouldHideOtherMembers = !can.update && !can.delete;

  return (
    <div>
      {can.invite && <InviteChipField team={team} />}
      <div className="mb-14 flex items-center gap-10 text-base">
        <GroupIcon className="icon-sm" />
        <Trans
          message="Members of :team"
          values={{team: team.name}}
        />
      </div>
      <AnimatePresence initial={false}>
        {members.map(member => {
          if (shouldHideOtherMembers && member.id !== user?.id) {
            return null;
          }
          return (
            <MemberListItem
              key={`${member.model_type}.${member.id}`}
              team={team}
              member={member}
            />
          );
        })}
        {shouldHideOtherMembers && (
          <div className="text-muted">
            <Trans
              message="And [one one other member|:count other members]"
              values={{count: members.length}}
            />
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface MemberListItemProps {
  member: TeamMember | TeamInvite;
  team: Team;
}
function MemberListItem({team, member}: MemberListItemProps) {
  return (
    <m.div
      initial={{x: '-100%', opacity: 0}}
      animate={{x: 0, opacity: 1}}
      exit={{x: '100%', opacity: 0}}
      transition={{type: 'tween', duration: 0.125}}
      className="mb-20 flex items-start gap-14 text-sm"
      key={`${member.model_type}.${member.id}`}
    >
      <img
        className="h-36 w-36 flex-shrink-0 rounded"
        src={member.avatar}
        alt=""
      />
      <div className="min-w-0 flex-auto items-center justify-between gap-14 md:flex">
        <div className="mb-10 overflow-hidden md:mb-0 md:mr-10">
          <div className="flex items-center justify-start gap-6">
            <div className="overflow-hidden text-ellipsis whitespace-nowrap">
              {member.display_name}
            </div>
            <MemberDisplayNameAppend team={team} member={member} />
          </div>
          <div className="text-muted">{member.email}</div>
        </div>
        <MemberActions team={team} member={member} />
      </div>
    </m.div>
  );
}

function usePermissions(team: Team) {
  const {user: authUser} = useAuth();
  const response = {update: false, invite: false, delete: false};
  const permissions = ['update', 'invite', 'delete'] as const;
  const authMember = team.members?.find(mb => mb.id === authUser?.id);

  if (authMember) {
    permissions.forEach(permission => {
      
      response[permission] =
        authMember.is_owner ||
        !!authMember.permissions?.find(
          p => p.name === `team_members.${permission}`,
        );
    });
  }

  return response;
}

interface MemberActionsProps {
  team: Team;
  member: TeamMember | TeamInvite;
}
function MemberActions({team, member}: MemberActionsProps) {
  const [selectedRole, setSelectedRole] = useState<number>(member.role_id);
  const changeRole = useChangeRole();
  const {user} = useAuth();

  const can = usePermissions(team);
  const isOwner = member.model_type === 'member' && member.is_owner;
  const isCurrentUser =
    member.model_type === 'member' && user?.id === member.id;

  const roleSelector =
    !can.update || isOwner || isCurrentUser ? (
      <div className="ml-auto text-muted first:capitalize">
        <Trans message={member.role_name} />
      </div>
    ) : (
      <RoleMenuTrigger
        className="ml-auto flex-shrink-0"
        size="xs"
        value={selectedRole}
        isDisabled={changeRole.isPending}
        onChange={roleId => {
          setSelectedRole(roleId);
          changeRole.mutate({
            roleId,
            teamId: team.id,
            member,
          });
        }}
      />
    );

  return (
    <>
      {roleSelector}
      {!isOwner && (isCurrentUser || can.delete) && (
        <RemoveMemberButton
          type={isCurrentUser ? 'leave' : 'remove'}
          member={member}
          team={team}
        />
      )}
    </>
  );
}

interface InviteChipFieldProps {
  team: Team;
}
function InviteChipField({team}: InviteChipFieldProps) {
  const {trans} = useTrans();
  const [chips, setChips] = useState<ChipValue[]>([]);
  const allEmailsValid = chips.every(chip => !chip.invalid);
  const displayWith = (chip: ChipValue) => chip.description || chip.name;
  const [selectedRole, setSelectedRole] = useState<number>();
  const inviteMembers = useInviteMembers();
  const {data} = useValueLists(['teamRoles']);

  useEffect(() => {
    if (!selectedRole && data?.teamRoles?.length) {
      setSelectedRole(data.teamRoles[0].id);
    }
  }, [data, selectedRole]);

  return (
    <div className="mb-30">
      <ChipField
        value={chips}
        onChange={setChips}
        displayWith={displayWith}
        validateWith={chip => {
          const invalid = !isEmail(chip.description);
          return {
            ...chip,
            invalid,
            errorMessage: invalid
              ? trans({message: 'Not a valid email'})
              : undefined,
          };
        }}
        placeholder={trans({message: 'Enter email addresses'})}
        label={<Trans message="Invite member" />}
      />

      <div className="mt-14 flex items-center justify-between gap-14">
        <RoleMenuTrigger onChange={setSelectedRole} value={selectedRole} />
        {chips.length && selectedRole ? (
          <Button
            variant="flat"
            color="primary"
            size="sm"
            disabled={inviteMembers.isPending || !allEmailsValid}
            onClick={() => {
              inviteMembers.mutate(
                {
                  emails: chips.map(c => displayWith(c)),
                  roleId: selectedRole,
                  teamId: team.id,
                },
                {
                  onSuccess: () => {
                    setChips([]);
                  },
                },
              );
            }}
          >
            <Trans message="Invite" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

interface RemoveMemberButtonProps {
  member: TeamMember | TeamInvite;
  team: Team;
  type: 'leave' | 'remove';
}
function RemoveMemberButton({
  member,
  team,
  type,
}: RemoveMemberButtonProps) {
  const removeMember = useRemoveMember();

  // const {close} = useDialogContext();
  return (
    <DialogTrigger
      type="modal"
      onClose={isConfirmed => {
        if (isConfirmed) {
          removeMember.mutate({
            teamId: team.id,
            memberId: member.id,
            memberType: member.model_type,
          });
          if (type === 'leave') {
            //close();
            toast(message('Left team'));
          }
        }
      }}
    >
      <IconButton
        size="md"
        className="flex-shrink-0 text-muted"
        disabled={removeMember.isPending}
      >
        {type === 'leave' ? <ExitToAppIcon /> : <CloseIcon />}
      </IconButton>
      {type === 'leave' ? (
        <LeaveTeamConfirmation />
      ) : (
        <RemoveMemberConfirmation member={member} />
      )}
    </DialogTrigger>
  );
}

interface RemoveMemberConfirmationProps {
  member: TeamMember | TeamInvite;
}
function RemoveMemberConfirmation({member}: RemoveMemberConfirmationProps) {
  return (
    <ConfirmationDialog
      isDanger
      title={<Trans message="Remove member" />}
      body={
        <div>
          <Trans
            message="Are you sure you want to remove `:name`?"
            values={{name: member.display_name}}
          />
          <div className="mt-8 font-semibold">
            <Trans
              message="All team resources created by `:name` will be transferred to team owner."
              values={{
                name: member.display_name,
              }}
            />
          </div>
        </div>
      }
      confirm={<Trans message="Remove" />}
    />
  );
}

interface RoleMenuTriggerProps {
  onChange: (value: number) => void;
  value?: number;
  size?: ButtonSize;
  className?: string;
  isDisabled?: boolean;
}
function RoleMenuTrigger({
  value,
  onChange,
  size = 'xs',
  className,
  isDisabled,
}: RoleMenuTriggerProps) {
  const {data} = useValueLists(['teamRoles']);
  const role = data?.teamRoles?.find(r => r.id === value);
  if (!value || !role || !data?.teamRoles) return null;

  return (
    <MenuTrigger
      selectionMode="single"
      selectedValue={value}
      onSelectionChange={newValue => {
        onChange(newValue as number);
      }}
    >
      <Button
        className={className}
        size={size}
        variant="flat"
        color="chip"
        disabled={isDisabled}
        endIcon={<ArrowDropDownIcon />}
      >
        <Trans message={role.name} />
      </Button>
      <Menu>
        {data.teamRoles.map(r => (
          <MenuItem value={r.id} key={r.id} description={r.description}>
            <Trans message={r.name} />
          </MenuItem>
        ))}
      </Menu>
    </MenuTrigger>
  );
}

interface MemberDisplayNameAppendProps {
  member: TeamMember | TeamInvite;
  team: Team;
}
function MemberDisplayNameAppend({
  member,
  team,
}: MemberDisplayNameAppendProps) {
  const {user} = useAuth();
  const can = usePermissions(team);

  if (user?.id === member.id) {
    return (
      <div className="font-medium">
        (<Trans message="You" />)
      </div>
    );
  }
  if (member.model_type === 'invite') {
    return (
      <div className="flex items-center gap-4">
        <div>·</div>
        <div className="font-medium">
          <Trans message="Invited" />
        </div>
        {can.invite ? (
          <>
            <div>·</div>
            <ResendInviteDialogTrigger member={member} team={team} />
          </>
        ) : null}
      </div>
    );
  }
  return null;
}

function ResendInviteDialogTrigger({
  member,
  team,
}: MemberDisplayNameAppendProps) {
  const resendInvite = useResendInvite();
  return (
    <DialogTrigger
      type="modal"
      onClose={isConfirmed => {
        if (isConfirmed) {
          resendInvite.mutate({
            teamId: team.id,
            inviteId: member.id as string,
          });
        }
      }}
    >
      <Button
        variant="link"
        size="sm"
        color="primary"
        disabled={resendInvite.isPending}
      >
        <Trans message="Resend invite" />
      </Button>
      <ConfirmationDialog
        title={<Trans message="Resend invite" />}
        body={
          <Trans message="Are you sure you want to send this invite again?" />
        }
        confirm={<Trans message="Send" />}
      />
    </DialogTrigger>
  );
}


interface DeleteTeamButtonProps {
  team: Team;
}
function DeleteTeamButton({ team}: DeleteTeamButtonProps) {
  const deleteTeam = useDeleteTeam();
  return (
    <DialogTrigger
      type="modal"
      onClose={confirmed => {
        if (confirmed) {
          deleteTeam.mutate({ id:  team.id});
        }
      }}
    >
      <Tooltip label={<Trans message="Delete Team Transaction" />}>
        <IconButton
          size="md"
          className="text-muted"
          disabled={deleteTeam.isPending}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
      <ConfirmationDialog
        title={<Trans message="Delete team" />}
        body={<Trans message="Are you sure you want to delete this Team Transaction?" />}
        confirm={<Trans message="Delete" />}
      />
    </DialogTrigger>
  );
}